// /c/{id}/preview: the instructor's look at what a student of the class is
// shown — the join page, then the class page — drawn from the class the
// instructor already has (its policy, consent and open assignments: what
// GET /v1/classes/join/{code} gives a student), with no request of a
// student's own. Each is framed as a browser's window with the address the
// student opens, side by side where the screen is wide enough and as tabs
// where it is not (the tab kept per browser). Nothing in them acts: a press
// inside a frame goes nowhere, but for the class page's tabs and folds. The class page lists no shares — an
// instructor's being no student's — unless "Show example shares" fills it
// with made-up ones, marked as such.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type ClassDetail, type JoinPreview, type ShareSummary } from "../api";
import { useKeptChoice, useKeptFlag } from "../kept";
import { useMedia } from "../media";
import { Link } from "../components/Nav";
import { Page, PageHead, PageLead, PageLoading, PageNotice } from "../components/Page";
import { panelId, tabId, Tabs } from "../components/Tabs";
import { t } from "../i18n";
import { StudentClass } from "../components/StudentClass";
import { JoinView } from "../components/JoinView";

type Tab = "joining" | "class";
const tabs: Tab[] = ["joining", "class"];

/** Wide enough for the two frames side by side. */
const SideBySide = "(min-width: 1100px)";

/** What a student is shown before joining, from the instructor's own copy of the class. */
function joinPreviewOf(cls: ClassDetail): JoinPreview {
  return {
    class: { id: cls.id, name: cls.name },
    consent: cls.consent,
    policy: cls.policy,
    assignments: (cls.assignments ?? []).filter((a) => !a.archived_at),
  };
}

/** Two shares made up to show a student's list filled: one live on the first open assignment, one ended. */
function exampleShares(cls: ClassDetail): ShareSummary[] {
  const now = Date.now();
  const open = (cls.assignments ?? []).find((a) => !a.archived_at);
  const me = { id: "example-student", name: "you" };
  return [
    {
      id: "example-live",
      title: t("classes:exampleLiveTitle"),
      source: "byj",
      owner: me,
      role: "owner",
      started_at: new Date(now - 12 * 60_000).toISOString(),
      participants: 1,
      live: true,
      class_id: cls.id,
      ...(open ? { assignment: { key: open.key, title: open.title } } : {}),
    },
    {
      id: "example-ended",
      title: t("classes:exampleEndedTitle"),
      source: "byj",
      owner: me,
      role: "owner",
      started_at: new Date(now - 26 * 3_600_000).toISOString(),
      // ended, not a share whose sidecar went (byj-fym)
      ended_at: new Date(now - 25 * 3_600_000).toISOString(),
      participants: 0,
      live: false,
      class_id: cls.id,
    },
  ];
}

/** A browser's window around a student's page: the address it is at, and the page, which nothing in acts. */
function Frame({ address, label, children, testid }: { address: string; label: string; children: ReactNode; testid: string }) {
  return (
    <figure className="preview-browser" data-testid={testid} aria-label={label}>
      <figcaption className="preview-browser-bar">
        <span className="preview-browser-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span className="preview-browser-address" title={address}>
          {address}
        </span>
      </figcaption>
      {/* a press goes nowhere: the page is shown, not used — but its tabs
          and folds turn, which only show more of it */}
      <div
        className="preview-browser-body"
        onClickCapture={(e) => {
          if (e.target instanceof Element && e.target.closest('[role="tab"], summary')) return;
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        {children}
      </div>
    </figure>
  );
}

export function ClassPreview({ id, load = api.class }: { id: string; load?: (id: string) => Promise<ClassDetail> }) {
  const [cls, setCls] = useState<ClassDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useKeptChoice<Tab>("class-preview.tab", "joining", tabs);
  const [withExample, setWithExample] = useKeptFlag("class-preview.example", false);
  const wide = useMedia(SideBySide, false);
  useEffect(() => {
    load(id).then(setCls, (err: unknown) => setError(err instanceof Error ? err.message : String(err)));
  }, [id, load]);
  const example = useMemo(() => (cls && withExample ? exampleShares(cls) : undefined), [cls, withExample]);

  if (error) {
    return (
      <PageNotice error={error}>
        <Link to="/classes">{t("classes:classesTitle")}</Link>
      </PageNotice>
    );
  }
  if (!cls) return <PageLoading />;
  if (cls.role !== "instructor") {
    return (
      <PageNotice message={t("classes:onlyInstructor")}>
        <Link to={`/c/${encodeURIComponent(cls.id)}`}>{cls.name}</Link>
      </PageNotice>
    );
  }

  const code = cls.join_code ?? "";
  const origin = typeof window !== "undefined" ? window.location.host : "";
  const joining = (
    <Frame address={`${origin}/c/join?code=${code}`} label={t("classes:previewJoining")} testid="preview-joining">
      <div className="narrow card stack preview-join">
        <JoinView preview={joinPreviewOf(cls)} />
      </div>
    </Frame>
  );
  const classPage = (
    <Frame address={`${origin}/c/${cls.id}`} label={t("classes:previewClassPage")} testid="preview-class">
      <StudentClass cls={{ ...cls, role: "student", members: undefined }} preview example={example} />
    </Frame>
  );

  return (
    <Page>
      <PageHead
        title={t("classes:previewTitle", { name: cls.name })}
        actions={
          <>
            <label className="switch-row example-switch">
              <input type="checkbox" role="switch" className="switch" checked={withExample} onChange={(e) => setWithExample(e.target.checked)} />
              <span>{t("classes:showExample")}</span>
            </label>
            <Link to={`/c/${encodeURIComponent(cls.id)}`} className="button">
              {t("classes:backToDashboard")}
            </Link>
          </>
        }
      />
      <PageLead>{withExample ? t("classes:previewLeadExample") : t("classes:previewLead")}</PageLead>
      {wide ? (
        // the student's way through, left to right: joining, then the class
        <div className="preview-pair">
          <section aria-labelledby="preview-joining-title">
            <h2 id="preview-joining-title" className="rows-head">
              1 · {t("classes:previewJoining")}
            </h2>
            {joining}
          </section>
          <section aria-labelledby="preview-class-title">
            <h2 id="preview-class-title" className="rows-head">
              2 · {t("classes:previewClassPage")}
            </h2>
            {classPage}
          </section>
        </div>
      ) : (
        <>
          <Tabs
            prefix="preview"
            label={t("classes:previewTabs")}
            value={tab}
            onChange={setTab}
            tabs={[
              { id: "joining", label: t("classes:previewJoining") },
              { id: "class", label: t("classes:previewClassPage") },
            ]}
          />
          <div role="tabpanel" id={panelId("preview", "joining")} aria-labelledby={tabId("preview", "joining")} hidden={tab !== "joining"}>
            {joining}
          </div>
          <div role="tabpanel" id={panelId("preview", "class")} aria-labelledby={tabId("preview", "class")} hidden={tab !== "class"}>
            {classPage}
          </div>
        </>
      )}
    </Page>
  );
}
